import React from 'react';

// Reusable shimmer animation wrapper - visible skeleton placeholder
const Shimmer = ({ className = '' }) => (
  <div className={`relative overflow-hidden bg-neutral-200 ${className}`}>
    <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
  </div>
);

// Skeleton for itinerary cards on the dashboard
export const SkeletonCard = ({ index = 0 }) => (
  <div
    className="bg-white rounded-2xl shadow-lg overflow-hidden border border-neutral-100"
    style={{ animationDelay: `${index * 0.08}s` }}
  >
    {/* Card Header - Image area skeleton */}
    <div className="relative h-40 overflow-hidden">
      <Shimmer className="w-full h-full" />
      {/* Status badge skeleton */}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="flex justify-between items-end">
          <Shimmer className="h-7 w-24 rounded-full" />
          <Shimmer className="h-6 w-28 rounded-full" />
        </div>
      </div>
    </div>

    {/* Card Content */}
    <div className="p-5 space-y-4">
      {/* Route Display skeleton */}
      <div className="relative">
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-center">
            <Shimmer className="w-8 h-8 rounded-full" />
            <Shimmer className="w-0.5 h-6 my-1" />
            <Shimmer className="w-8 h-8 rounded-full" />
          </div>
          <div className="flex-1 space-y-3">
            <div>
              <Shimmer className="h-3 w-10 rounded mb-1" />
              <Shimmer className="h-5 w-32 rounded" />
            </div>
            <div>
              <Shimmer className="h-3 w-8 rounded mb-1" />
              <Shimmer className="h-5 w-28 rounded" />
            </div>
          </div>
        </div>
      </div>

      {/* Travel Dates skeleton */}
      <div className="flex items-center gap-3 bg-neutral-50 p-3 rounded-xl">
        <Shimmer className="w-10 h-10 rounded-xl" />
        <div className="flex-1">
          <Shimmer className="h-3 w-20 rounded mb-1" />
          <Shimmer className="h-5 w-36 rounded" />
        </div>
      </div>

      {/* Interests skeleton */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Shimmer className="w-4 h-4 rounded" />
          <Shimmer className="h-3 w-16 rounded" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Shimmer className="h-6 w-16 rounded-full" />
          <Shimmer className="h-6 w-20 rounded-full" />
          <Shimmer className="h-6 w-14 rounded-full" />
        </div>
      </div>
    </div>

    {/* Card Actions skeleton */}
    <div className="p-4 bg-neutral-50 border-t border-neutral-100 flex gap-2">
      <Shimmer className="flex-1 h-10 rounded-xl" />
      <Shimmer className="w-10 h-10 rounded-xl" />
      <Shimmer className="w-10 h-10 rounded-xl" />
      <Shimmer className="w-10 h-10 rounded-xl" />
    </div>
  </div>
);

// Skeleton for member itinerary cards (shared trips)
export const SkeletonMemberCard = ({ index = 0 }) => (
  <div
    className="bg-white rounded-2xl shadow-lg overflow-hidden ring-2 ring-neutral-100"
    style={{ animationDelay: `${index * 0.08}s` }}
  >
    {/* Card Header skeleton */}
    <div className="relative p-5 overflow-hidden">
      <Shimmer className="absolute inset-0" />
      <div className="relative flex justify-between items-start">
        <Shimmer className="h-7 w-24 rounded-full" />
        <Shimmer className="h-5 w-16 rounded-full" />
      </div>
    </div>

    {/* Card Content */}
    <div className="p-5 space-y-4">
      {/* Owner Info skeleton */}
      <div className="bg-neutral-50 rounded-xl p-3 flex items-center gap-3">
        <Shimmer className="w-8 h-8 rounded-full" />
        <Shimmer className="h-4 w-32 rounded" />
      </div>

      {/* Route Display skeleton */}
      <div className="relative">
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-center">
            <Shimmer className="w-7 h-7 rounded-full" />
            <Shimmer className="w-0.5 h-5 my-1" />
            <Shimmer className="w-7 h-7 rounded-full" />
          </div>
          <div className="flex-1 space-y-2">
            <div>
              <Shimmer className="h-3 w-10 rounded mb-1" />
              <Shimmer className="h-4 w-24 rounded" />
            </div>
            <div>
              <Shimmer className="h-3 w-8 rounded mb-1" />
              <Shimmer className="h-4 w-20 rounded" />
            </div>
          </div>
        </div>
      </div>

      {/* Trip Dates skeleton */}
      <div className="flex items-center gap-2 bg-neutral-50 p-2 rounded-lg">
        <Shimmer className="w-4 h-4 rounded" />
        <Shimmer className="h-4 w-32 rounded" />
      </div>
    </div>

    {/* Card Actions skeleton */}
    <div className="p-4 bg-neutral-50 border-t border-neutral-100 flex gap-2">
      <Shimmer className="flex-1 h-10 rounded-xl" />
    </div>
  </div>
);

// Grid of skeleton cards
export const SkeletonCardGrid = ({ count = 6, type = 'owner' }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {Array.from({ length: count }).map((_, index) => (
      type === 'member' ? (
        <SkeletonMemberCard key={index} index={index} />
      ) : (
        <SkeletonCard key={index} index={index} />
      )
    ))}
  </div>
);

// Linear progress bar with label
export const ProgressBar = ({
  progress,
  label = '',
  showPercentage = true,
  size = 'md',
  variant = 'gradient'
}) => {
  const sizeClasses = {
    sm: 'h-1.5',
    md: 'h-2.5',
    lg: 'h-4'
  };

  const variantClasses = {
    gradient: 'bg-gradient-to-r from-primary-500 via-accent-500 to-secondary-500',
    primary: 'bg-primary-500',
    success: 'bg-secondary-500',
    warning: 'bg-golden-500'
  };

  return (
    <div className="w-full">
      {(label || showPercentage) && (
        <div className="flex justify-between items-center mb-2">
          {label && <span className="text-sm font-medium text-neutral-700">{label}</span>}
          {showPercentage && (
            <span className="text-sm font-semibold text-neutral-800">{Math.round(progress)}%</span>
          )}
        </div>
      )}
      <div className={`w-full bg-neutral-200 rounded-full overflow-hidden ${sizeClasses[size]}`}>
        <div
          className={`${sizeClasses[size]} rounded-full transition-all duration-500 ease-out ${variantClasses[variant]}`}
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        />
      </div>
    </div>
  );
};

// Detailed progress indicator for AI generation
export const AIGenerationProgress = ({
  progress,
  currentStep,
  totalSteps,
  stepLabel
}) => (
  <div className="bg-white rounded-xl p-4 shadow-md border border-neutral-100">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center">
          <span className="text-white text-xs font-bold">{currentStep}</span>
        </div>
        <div>
          <p className="text-sm font-semibold text-neutral-800">{stepLabel}</p>
          <p className="text-xs text-neutral-500">Step {currentStep} of {totalSteps}</p>
        </div>
      </div>
      <span className="text-lg font-bold text-primary-600">{Math.round(progress)}%</span>
    </div>

    {/* Main progress bar */}
    <div className="relative h-3 bg-neutral-100 rounded-full overflow-hidden">
      {/* Background segments */}
      <div className="absolute inset-0 flex">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div key={i} className="flex-1 border-r border-neutral-200 last:border-r-0" />
        ))}
      </div>
      {/* Progress fill */}
      <div
        className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary-500 via-accent-500 to-secondary-500 rounded-full transition-all duration-700 ease-out"
        style={{ width: `${progress}%` }}
      >
        {/* Animated shimmer effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer-fast" />
      </div>
    </div>

    {/* Step indicators */}
    <div className="flex justify-between mt-2">
      {Array.from({ length: totalSteps }).map((_, i) => (
        <div
          key={i}
          className={`w-2 h-2 rounded-full transition-all duration-300 ${
            i < currentStep
              ? 'bg-secondary-500'
              : i === currentStep - 1
                ? 'bg-primary-500 ring-2 ring-primary-200'
                : 'bg-neutral-300'
          }`}
        />
      ))}
    </div>
  </div>
);

export default SkeletonCard;
